import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Send,
  Sparkles,
  Trash2,
  Loader2,
  Bot,
  User as UserIcon,
} from "lucide-react";

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div
      data-testid={`chat-msg-${msg.role}`}
      className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}
    >
      <span
        className={`shrink-0 mt-0.5 grid place-items-center h-7 w-7 rounded-full ${
          isUser
            ? "bg-paper-2 border border-rule text-ink-soft"
            : "bg-forest text-paper"
        }`}
      >
        {isUser ? <UserIcon size={12} /> : <Bot size={12} />}
      </span>
      <div
        className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-forest text-paper rounded-tr-sm"
            : "bg-white border border-rule text-ink rounded-tl-sm"
        }`}
      >
        {msg.text}
      </div>
    </div>
  );
}

const SUGGESTIONS = [
  "Resume mi planteamiento en una sola oración.",
  "Sugiere 3 autores clave para mi marco teórico.",
  "¿Cómo mejoro la coherencia entre objetivos y metodología?",
];

export default function AIChatPanel({ projectId, onUsageChange, disabled }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/chat`);
      setMessages(data.messages || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setSending(true);
    setInput("");
    // Optimistic user bubble
    const tempUserId = `tmp_${Date.now()}`;
    setMessages((m) => [
      ...m,
      { message_id: tempUserId, role: "user", text, created_at: new Date().toISOString() },
    ]);
    try {
      const { data } = await api.post(`/projects/${projectId}/chat`, { text });
      setMessages((m) => {
        const without = m.filter((x) => x.message_id !== tempUserId);
        return [...without, data.user_message, data.assistant_message];
      });
      onUsageChange?.({
        pages_generated: data.pages_generated,
        pages_limit: data.pages_limit,
      });
    } catch (e) {
      setMessages((m) => m.filter((x) => x.message_id !== tempUserId));
      const detail = e?.response?.data?.detail || "Error al consultar al copiloto";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("¿Borrar todo el historial de chat de este proyecto?")) return;
    try {
      await api.delete(`/projects/${projectId}/chat`);
      setMessages([]);
      toast.success("Historial borrado");
    } catch (e) {
      toast.error("No se pudo borrar");
    }
  };

  return (
    <div className="flex flex-col h-full bg-paper-2/30" data-testid="ai-chat-panel">
      <div className="px-4 py-3 border-b border-rule flex items-center gap-2 bg-paper-2/60">
        <span className="grid place-items-center h-8 w-8 rounded-full bg-forest text-paper">
          <Sparkles size={14} />
        </span>
        <div className="leading-tight flex-1 min-w-0">
          <p className="font-serif text-[15px] text-ink">Copiloto de tesis</p>
          <p className="text-[10.5px] text-ink-soft">Claude Sonnet 4.6 · tutor académico</p>
        </div>
        {messages.length > 0 && (
          <button
            data-testid="chat-clear-btn"
            onClick={clearAll}
            aria-label="Borrar historial"
            title="Borrar historial"
            className="h-7 w-7 grid place-items-center rounded-full hover:bg-paper-2 text-ink-soft hover:text-[#B6533F]"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>

      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3"
        data-testid="chat-scroller"
      >
        {loading ? (
          <p className="text-[12px] text-ink-soft italic">Cargando conversación…</p>
        ) : messages.length === 0 ? (
          <div className="rounded-xl border border-rule bg-white p-4">
            <p className="font-serif text-[15px] text-ink leading-tight">
              Pregúntale a tu copiloto.
            </p>
            <p className="mt-1 text-[12.5px] text-ink-soft">
              Conoce tu título, carrera y problema. Pídele ideas, contraargumentos o reescrituras.
            </p>
            <div className="mt-3 flex flex-col gap-1.5">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  data-testid={`chat-suggestion-${i}`}
                  onClick={() => send(s)}
                  disabled={disabled}
                  className="text-left text-[12px] text-forest hover:text-forest-dark px-2.5 py-1.5 rounded-md border border-rule bg-paper-2/40 hover:bg-paper-2 transition-colors disabled:opacity-60"
                >
                  “{s}”
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m) => <MessageBubble key={m.message_id} msg={m} />)
        )}
        {sending && (
          <div className="flex gap-2.5" data-testid="chat-typing">
            <span className="shrink-0 mt-0.5 grid place-items-center h-7 w-7 rounded-full bg-forest text-paper">
              <Bot size={12} />
            </span>
            <div className="rounded-2xl px-3.5 py-2.5 bg-white border border-rule text-[13px] text-ink-soft inline-flex items-center gap-2">
              <Loader2 size={13} className="animate-spin" />
              Pensando…
            </div>
          </div>
        )}
      </div>

      <div className="p-3 border-t border-rule bg-white">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          data-testid="chat-form"
          className="flex items-end gap-2"
        >
          <textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={disabled ? "Define el contexto del proyecto para chatear" : "Escribe tu pregunta…"}
            rows={2}
            disabled={disabled || sending}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            className="flex-1 px-3 py-2 rounded-lg border border-rule bg-paper-2/30 focus:bg-white text-[13px] resize-none outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 max-h-32 disabled:opacity-60"
          />
          <button
            data-testid="chat-send-btn"
            type="submit"
            disabled={!input.trim() || sending || disabled}
            className="grid place-items-center h-10 w-10 rounded-full bg-forest text-paper hover:bg-forest-dark disabled:opacity-50 transition-colors"
            aria-label="Enviar"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </form>
        <p className="mt-1.5 text-[10.5px] text-ink-soft/80 text-center">
          Cada respuesta cuenta como 1 página IA (plan gratis: 3 máx).
        </p>
      </div>
    </div>
  );
}
